import styled from "styled-components"
import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Menu, X, Globe, Check, MessageSquare } from 'lucide-react';
import { purpleColors, slateColors } from '../ContentPages/AppLandingPage/styles';

const Container = styled.header<{ $menuOpen: boolean; $scrolled: boolean }>`
  position: sticky;
  top: 0;
display: flex;
  padding: 1rem 1rem;
justify-content: space-between;
align-items: center;
  background: ${props => props.$scrolled ? 'rgba(255, 255, 255, 0.95)' : 'white'};
  backdrop-filter: ${props => props.$scrolled ? 'blur(10px)' : 'none'};
  border-bottom: 1px solid ${props => props.$scrolled ? slateColors[200] : 'transparent'};
  z-index: 1000;
  transition: all 0.2s ease;
  box-shadow: ${props => props.$scrolled ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' : 'none'};
  
  @media (min-width: 640px) {
    padding: 1rem 1.5rem;
  }
  
  @media (min-width: 768px) {
    padding: 1.25rem 2rem;
  }
  
  @media (min-width: 1024px) {
    padding: 1.25rem 3rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  width: 100%;
  max-width: 80rem;
  margin: 0 auto;
`;

const LogoWrapper = styled(Link)`
  cursor: pointer;
  text-decoration: none;
  display: flex;
  align-items: center;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.8;
  }
  
  img {
    height: 36px;
    width: auto;
    
    @media (min-width: 640px) {
      height: 40px;
    }
    
    @media (min-width: 1024px) {
      height: 44px;
    }
  }
`;

const MenuButton = styled.button<{ $menuOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${slateColors[700]};
  transition: color 0.2s;
  border-radius: 0.5rem;
  
  &:hover {
    color: ${slateColors[900]};
    background: ${slateColors[50]};
  }
  
  @media (min-width: 768px) {
    display: none;
  }
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const DesktopMenu = styled.nav`
  display: none;
  gap: 2rem;
  align-items: center;
  
  @media (min-width: 768px) {
    display: flex;
  }
`;

const MenuItemLink = styled(Link)<{ $isActive?: boolean }>`
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.5;
  color: ${props => props.$isActive ? purpleColors[600] : slateColors[700]};
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s;
  position: relative;
  padding: 0.5rem 0;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${props => props.$isActive ? '100%' : '0'};
    height: 2px;
    background: ${purpleColors[600]};
    transition: width 0.2s;
  }
  
  &:hover {
    color: ${purpleColors[600]};
    
    &::after {
      width: 100%;
    }
  }
  
  @media (min-width: 1024px) {
    font-size: 1rem;
  }
`;

const DownloadButton = styled.a`
  padding: 0.625rem 1.25rem;
  background: linear-gradient(to right, ${purpleColors[600]}, ${purpleColors[700]});
  color: white;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &:hover {
    background: linear-gradient(to right, ${purpleColors[700]}, ${purpleColors[600]});
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 1rem;
    height: 1rem;
  }
  
  @media (min-width: 1024px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9375rem;
  }
`;

const FeedbackButton = styled.a`
  padding: 0.625rem 1.25rem;
  background: linear-gradient(to right, #eab308, #ca8a04);
  color: white;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &:hover {
    background: linear-gradient(to right, #ca8a04, #eab308);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 1rem;
    height: 1rem;
  }
  
  @media (min-width: 1024px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9375rem;
  }
`;

const MenuOverlay = styled.div<{ $menuOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 999;
  display: ${props => props.$menuOpen ? 'flex' : 'none'};
  align-items: flex-start;
  justify-content: flex-end;
  padding-top: 4.5rem;
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileMenu = styled.div<{ $menuOpen: boolean }>`
  background: white;
  width: 85%;
  max-width: 20rem;
  height: calc(100vh - 4.5rem);
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  box-shadow: -4px 0 6px -1px rgba(0, 0, 0, 0.1), -2px 0 4px -1px rgba(0, 0, 0, 0.06);
  transform: ${props => props.$menuOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`;

const MobileMenuItemLink = styled(Link)`
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.5;
  color: ${slateColors[700]};
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${slateColors[100]};
  
  &:hover {
    color: ${purpleColors[600]};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const MobileDownloadButton = styled.a`
  padding: 0.875rem 1.5rem;
  background: linear-gradient(to right, ${purpleColors[600]}, ${purpleColors[700]});
  color: white;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-decoration: none;
  margin-top: 0.5rem;
  
  &:hover {
    background: linear-gradient(to right, ${purpleColors[700]}, ${purpleColors[600]});
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
  }
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const MobileFeedbackButton = styled.a`
  padding: 0.875rem 1.5rem;
  background: linear-gradient(to right, #eab308, #ca8a04);
  color: white;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-decoration: none;
  margin-top: 0.5rem;
  
  &:hover {
    background: linear-gradient(to right, #ca8a04, #eab308);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
  }
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const LanguageDropdownContainer = styled.div`
  position: relative;
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: 1px solid ${slateColors[200]};
  border-radius: 0.5rem;
  cursor: pointer;
  color: ${slateColors[700]};
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${purpleColors[300]};
    background: ${slateColors[50]};
    color: ${slateColors[900]};
  }
  
  svg {
    width: 1rem;
    height: 1rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 0.9375rem;
  }
`;

const LanguageDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: white;
  border: 1px solid ${slateColors[200]};
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  display: ${props => props.$isOpen ? 'block' : 'none'};
  width: max-content;
  z-index: 100;
`;

const LanguageOption = styled.button<{ $isActive?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: ${props => props.$isActive ? slateColors[50] : 'transparent'};
  border: none;
  cursor: pointer;
  color: ${props => props.$isActive ? purpleColors[600] : slateColors[700]};
  font-size: 0.9375rem;
  font-weight: ${props => props.$isActive ? '600' : '500'};
  transition: all 0.2s;
  text-align: left;
  white-space: nowrap;
  
  &:hover {
    background: ${slateColors[50]};
    color: ${purpleColors[600]};
  }
  
  &:first-child {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }
  
  &:last-child {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }
  
  svg {
    width: 1rem;
    height: 1rem;
    color: ${purpleColors[600]};
    flex-shrink: 0;
  }
`;

const MobileLanguageSection = styled.div`
  padding-top: 1rem;
  padding-bottom: 3rem;
  margin-top: 1rem;
  border-top: 1px solid ${slateColors[200]};
  flex-shrink: 0;
`;

const MobileLanguageTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${slateColors[500]};
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const MobileLanguageOption = styled.button<{ $isActive?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${props => props.$isActive ? purpleColors[600] : slateColors[700]};
  font-size: 1rem;
  font-weight: ${props => props.$isActive ? '600' : '500'};
  transition: all 0.2s;
  text-align: left;
  
  &:hover {
    color: ${purpleColors[600]};
  }
  
  svg {
    width: 1rem;
    height: 1rem;
    color: ${purpleColors[600]};
  }
`;

const languages = [
  { code: 'en', name: 'English', abbr: 'EN' },
  { code: 'nl', name: 'Nederlands', abbr: 'NL' },
  { code: 'de', name: 'Deutsch', abbr: 'DE' },
  { code: 'fr', name: 'Français', abbr: 'FR' },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isAppPage = location.pathname === '/app';

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setLanguageDropdownOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setLanguageDropdownOpen(false);
      }
    };

    if (languageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [languageDropdownOpen]);

  const { t, i18n } = useTranslation();
  
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const toggleLanguageDropdown = () => {
    setLanguageDropdownOpen(!languageDropdownOpen);
  };

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setLanguageDropdownOpen(false);
  };

  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === i18n.language) || languages[0];
  };

  const getTranslationFeedbackMailto = () => {
    const currentLang = getCurrentLanguage();
    const currentUrl = window.location.href;
    const subject = t('header.feedbackEmailSubject');
    const body = `${t('header.feedbackEmailLanguage')}: ${currentLang.name} (${currentLang.abbr})
${t('header.feedbackEmailUrl')}: ${currentUrl}

${t('header.feedbackEmailText')}

${t('header.feedbackEmailSuggestion')}

${t('header.feedbackEmailScreenshot')}`;
    
    return `mailto:feedback@chuuchuu.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // Determine download button href based on current route
  const downloadHref = isAppPage ? '/app#download' : '/app';

  // Check if a link is active
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <>
      <Container $menuOpen={menuOpen} $scrolled={scrolled}>
        <HeaderContent>
          <LogoWrapper to="/">
            <img src="/images/chuuchuu-logo.svg" alt="chuuchuu logo" />
      </LogoWrapper>

      <DesktopMenu>
            <MenuItemLink to="/data" $isActive={isActive('/data')}>{t('header.data')}</MenuItemLink>
            <MenuItemLink to="/contact" $isActive={isActive('/contact')}>{t('header.contact')}</MenuItemLink>
            <MenuItemLink to="/2025wrapped" $isActive={isActive('/2025wrapped')}>{t('header.wrapped2025')}</MenuItemLink>
            <MenuItemLink to="/heatwave" $isActive={isActive('/heatwave')}>{t('header.heatwave')}</MenuItemLink>
            
            <LanguageDropdownContainer ref={languageDropdownRef}>
              <LanguageButton onClick={toggleLanguageDropdown}>
                <Globe />
                <span>{getCurrentLanguage().abbr}</span>
              </LanguageButton>
              <LanguageDropdown $isOpen={languageDropdownOpen}>
                {languages.map((lang) => (
                  <LanguageOption
                    key={lang.code}
                    $isActive={i18n.language === lang.code}
                    onClick={() => changeLanguage(lang.code)}
                  >
                    <span>{lang.abbr} — {lang.name}</span>
                    {i18n.language === lang.code && <Check />}
                  </LanguageOption>
                ))}
              </LanguageDropdown>
            </LanguageDropdownContainer>
            
        <DownloadButton href={downloadHref}>
          <Download />
          {t('header.getTheApp')}
        </DownloadButton>
      </DesktopMenu>

          <MenuButton $menuOpen={menuOpen} onClick={toggleMenu} aria-label={t('header.toggleMenu')}>
            {menuOpen ? <X /> : <Menu />}
      </MenuButton>
        </HeaderContent>
      </Container>
            
      <MenuOverlay $menuOpen={menuOpen} onClick={toggleMenu}>
        <MobileMenu $menuOpen={menuOpen} onClick={(e) => e.stopPropagation()}>
          <MobileMenuItemLink to="/data" onClick={toggleMenu}>{t('header.data')}</MobileMenuItemLink>
          <MobileMenuItemLink to="/contact" onClick={toggleMenu}>{t('header.contact')}</MobileMenuItemLink>
          <MobileMenuItemLink to="/2025wrapped" onClick={toggleMenu}>{t('header.wrapped2025')}</MobileMenuItemLink>
          <MobileMenuItemLink to="/heatwave" onClick={toggleMenu}>{t('header.heatwave')}</MobileMenuItemLink>
          
          <MobileDownloadButton href={downloadHref} onClick={toggleMenu}>
            <Download />
            {t('header.getTheApp')}
          </MobileDownloadButton>
          
          <MobileLanguageSection>
            <MobileLanguageTitle>Language</MobileLanguageTitle>
            {languages.map((lang) => (
              <MobileLanguageOption
                key={lang.code}
                $isActive={i18n.language === lang.code}
                onClick={() => {
                  changeLanguage(lang.code);
                  toggleMenu();
                }}
              >
                <span>{lang.abbr} — {lang.name}</span>
                {i18n.language === lang.code && <Check />}
              </MobileLanguageOption>
            ))}
          </MobileLanguageSection>
        </MobileMenu>
        </MenuOverlay>
    </>
  );
};
  
export default Header;